import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { companySignatureBlock, hasCompanySignature } from '@immo/api-contract';
import type { MailboxProvider, SendEmailResponse } from '@immo/api-contract';
import { Button, cn, InfoBox, Modal, useToast } from '@immo/ui-kit';
import { apiClient } from '@/lib/auth/api-client';
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  EyeIcon,
  FileIcon,
  MailIcon,
  PaperclipIcon,
  SendIcon,
  WarningTriangleIcon,
  XIcon,
} from '@/components/layout/icons';
import { ChipLineField } from '@/components/email-template/chip-line-field';
import type { ChipInsertHandle } from '@/components/email-template/email-template-chips';
import { collectOpenFields, selectOpenField } from '@/components/email-template/open-field-interaction';
import { toSendableBody } from '@/components/email-template/personal-link';
import { MailboxAvatar } from '@/components/mailbox-avatar';
import { sanitiseEmailHtml } from '@/lib/email/sanitise-email-html';
import { useTranslations } from '@/i18n/use-translations';
import { usePlaceholderLabel } from '@/pages/settings/email-template-columns';
import { ComposerAttachments, formatBytes } from './composer-attachments';
import { ComposerBodyEditor, type ComposerBodyEditorHandle } from './composer-body-editor';
import { ComposerFieldRow } from './composer-field-row';
import { ComposerRecipientField, isValidEmail } from './composer-recipient-field';
import { ComposerSignature } from './composer-signature';
import { TemplateMenu } from './template-menu';
import type { ComposerMode, ComposerSeed, MailAddress, OutboundAttachment } from './use-composers';
import { useTemplateInsertion } from './use-template-insertion';

// The combined-attachment client guard — 25 MiB, matching the backend's brokered
// decoded-bytes cap (MAX_COMBINED_ATTACHMENT_BYTES = 25 * 1024 * 1024) byte-for-byte.
// Kept as a local literal (the backend const lives in the backend module, not a
// shared package the web-app imports); the two are asserted equal by tests.
//
// It is the UPPER bound, not the only one: a directly-connected Google mailbox
// caps at ~18 MiB, because Gmail applies its 25 MB ceiling to the base64-encoded
// message (IMM-530.3). The client cannot know which stack serves this member, so
// it pre-empts only the case every stack refuses and lets the backend's 422 carry
// the real limit — which is why the copy below is rendered from `details.limitBytes`
// rather than restating a number that is right for only one stack.
export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

/**
 * The limit the backend actually refused against, off the 422's `details`. A
 * direct Google mailbox reports ~18 MiB where the brokered one reports 25 MiB, so
 * restating the client constant here would tell a member their 20 MB attachment
 * exceeds a 25 MB limit. Falls back to the client guard when the shape is
 * unexpected — a slightly wrong number beats an empty one.
 */
function refusedLimitBytes(body: unknown): number {
  const details = (body as { error?: { details?: { limitBytes?: unknown } } } | null)?.error?.details;

  return typeof details?.limitBytes === 'number' ? details.limitBytes : MAX_ATTACHMENT_BYTES;
}

/**
 * Renders an attachment cap the way this copy has always described it. NOT
 * `formatBytes`, which is base-1000 and would turn the 25 MiB constant into
 * „26,2 MB" — the caps are MiB values that the copy has always called „MB".
 */
function formatLimit(bytes: number): string {
  return `${Math.round(bytes / (1024 * 1024))} MB`;
}

const SUBJECT_MAX_LENGTH = 255;

const TITLE_KEY: Record<ComposerMode, string> = {
  new: 'title-new',
  reply: 'title-reply',
  'reply-all': 'title-reply-all',
  forward: 'title-forward',
};

// Sender-badge brand + status-dot colour for the preview (how the mail lands in
// the recipient's inbox — the connected mailbox's provider).
const PROVIDER_BRAND: Record<MailboxProvider, string> = { GOOGLE: 'Gmail', MICROSOFT: 'Outlook', IMAP: 'IMAP' };
const PROVIDER_DOT: Record<MailboxProvider, string> = {
  GOOGLE: 'bg-danger-500',
  MICROSOFT: 'bg-interactive',
  IMAP: 'bg-text-tertiary',
};

// Uppercase file extension for the attachment chip's subtitle ("PDF · 1.1 MB").
function fileExtLabel(name: string): string {
  const dot = name.lastIndexOf('.');

  return dot === -1 ? 'FILE' : name.slice(dot + 1).toUpperCase();
}

export type EmailComposerProps = {
  readonly workspaceId: string;
  readonly mode: ComposerMode;
  readonly seed: ComposerSeed;
  readonly connectedEmail: string | null;
  // The connected mailbox's provider — badges the sender in the preview. Optional;
  // omitted (no badge) when the caller doesn't have it.
  readonly provider?: MailboxProvider | null;
  readonly minimised: boolean;
  // Slot index among the currently-minimised composers, so their docked bars sit
  // side by side (0 = right-most) instead of stacking on top of each other.
  readonly dockOffset?: number;
  readonly onMinimiseToggle: () => void;
  readonly onClose: () => void;
  readonly onSent?: (result: SendEmailResponse) => void;
  // Inline reply within the thread panel (design 02): drops the floating-window
  // chrome (title bar, minimise/close, shadow, viewport cap) so the composer reads
  // as a reply box docked under the conversation rather than a modal.
  readonly embedded?: boolean;
};

type FieldErrors = {
  to?: string;
  cc?: string;
  bcc?: string;
  subject?: string;
  attachments?: string;
  openFields?: string;
};

// One rule for 'this composer has a signature': the block shows exactly when the send appends it.
export const hasSignature = hasCompanySignature;

// The full body the send carries and the preview renders: the rich-text body HTML
// + signature + quoted history, in that order, each part omitted when empty.
export function assembleBody(params: {
  readonly typedHtml: string;
  readonly signatureHtml?: string | null;
  readonly quotedHtml?: string;
}): string {
  const parts: string[] = [];

  if (params.typedHtml.trim() !== '') parts.push(params.typedHtml);

  if (hasSignature(params.signatureHtml)) {
    parts.push(companySignatureBlock(params.signatureHtml));
  }

  if (params.quotedHtml !== undefined && params.quotedHtml.trim() !== '') {
    parts.push(params.quotedHtml);
  }

  return parts.join('');
}

// Read a File as base64 (strip the `data:...;base64,` prefix FileReader adds).
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      const comma = result.indexOf(',');
      resolve(comma === -1 ? '' : result.slice(comma + 1));
    };

    reader.onerror = () => reject(reader.error ?? new Error('read-failed'));
    reader.readAsDataURL(file);
  });
}

// Decoded byte length of a base64 string (without decoding it): 3 bytes per 4
// chars, minus the `=` padding.
function base64DecodedLength(base64: string): number {
  const len = base64.length;

  if (len === 0) return 0;

  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;

  return (len / 4) * 3 - padding;
}

function firstInvalidAddress(addresses: ReadonlyArray<MailAddress>): string | null {
  for (const address of addresses) {
    if (!isValidEmail(address.email)) return address.email;
  }

  return null;
}

// Same recipient set (by address, in order) — drives the composer's dirty check.
function sameMailAddresses(a: ReadonlyArray<MailAddress>, b: ReadonlyArray<MailAddress>): boolean {
  return a.length === b.length && a.every((address, index) => address.email === b[index]?.email);
}

export function EmailComposer({
  workspaceId,
  mode,
  seed,
  connectedEmail,
  provider,
  minimised,
  dockOffset = 0,
  onMinimiseToggle,
  onClose,
  onSent,
  embedded = false,
}: EmailComposerProps) {
  const t = useTranslations('pages.workspace.mailboxComposer');
  const tRoot = useTranslations();
  const toast = useToast();

  const [to, setTo] = useState<ReadonlyArray<MailAddress>>(seed.to ?? []);
  const [cc, setCc] = useState<ReadonlyArray<MailAddress>>(seed.cc ?? []);
  const [bcc, setBcc] = useState<ReadonlyArray<MailAddress>>([]);
  const [subject, setSubject] = useState<string>(seed.subject ?? '');
  const [bodyHtml, setBodyHtml] = useState(seed.bodyHtml ?? '');
  const [files, setFiles] = useState<ReadonlyArray<File>>([]);
  const [showCcBcc, setShowCcBcc] = useState((seed.cc ?? []).length > 0);
  const [sending, setSending] = useState(false);
  const [providerRefusal, setProviderRefusal] = useState<string | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [openFieldFocus, setOpenFieldFocus] = useState(0);
  const subjectRef = useRef<ChipInsertHandle>(null);
  const bodyRef = useRef<ComposerBodyEditorHandle>(null);
  const placeholderLabel = usePlaceholderLabel();
  // `ChipLineField` seeds through `innerHTML`, so the seed is escaped as text once.
  const [subjectHtml, setSubjectHtml] = useState(() => {
    const holder = document.createElement('div');

    holder.textContent = seed.subject ?? '';

    return holder.innerHTML;
  });
  const insertTemplate = useTemplateInsertion({
    workspaceId,
    templateContext: seed.templateContext,
    connectedEmail,
    to,
    subjectRef,
    bodyRef,
  });

  // The attachment file picker is owned here so both the toolbar's paperclip and
  // the footer's "attach" button trigger the one hidden input.
  const fileInputRef = useRef<HTMLInputElement>(null);
  const openFilePicker = () => fileInputRef.current?.click();
  const addFiles = (list: FileList | null) => {
    if (list === null || list.length === 0) return;

    // Snapshot the FileList NOW: the change handler resets `input.value` right
    // after this call, which empties the live FileList. Deferring `Array.from`
    // into the state updater (which React runs on the next render) would read it
    // back already empty — so single, one-at-a-time attachments would silently
    // add nothing. Copying synchronously here pins the files before the reset.
    const incoming = Array.from(list);

    setFiles((current) => [...current, ...incoming]);
  };

  const forwardAttachments = useMemo(() => seed.forwardAttachments ?? [], [seed.forwardAttachments]);
  const forwardBaselineBytes = useMemo(
    () => forwardAttachments.reduce((sum, attachment) => sum + base64DecodedLength(attachment.contentBase64), 0),
    [forwardAttachments],
  );

  const assembledHtml = useMemo(
    () =>
      assembleBody({
        typedHtml: bodyHtml,
        signatureHtml: seed.signatureHtml ?? null,
        ...(seed.quotedHtml !== undefined ? { quotedHtml: seed.quotedHtml } : {}),
      }),
    [bodyHtml, seed.signatureHtml, seed.quotedHtml],
  );

  // The preview mixes two trust origins. Our own draft + signature are trusted
  // (their images are our presigned assets), the quoted original is inbound mail
  // and keeps the tracking-pixel block. Sanitised separately, then concatenated —
  // one `allowRemoteImages` for the whole string cannot express both. `assembledHtml`
  // above stays as-is: it is what confirmSend posts and what the preview button's
  // disabled check reads; only the preview's rendering input changes here.
  const previewHtml = useMemo(() => {
    const own = assembleBody({ typedHtml: bodyHtml, signatureHtml: seed.signatureHtml ?? null });
    const quoted = seed.quotedHtml ?? '';

    return (
      sanitiseEmailHtml(own, { allowRemoteImages: true }).html +
      (quoted.trim() === '' ? '' : sanitiseEmailHtml(quoted).html)
    );
  }, [bodyHtml, seed.signatureHtml, seed.quotedHtml]);

  // Any change from the seeded starting point makes the composer "dirty", so closing
  // the side panel (Esc / X / backdrop) asks for confirmation instead of discarding.
  const isDirty =
    bodyHtml.trim() !== (seed.bodyHtml ?? '').trim() ||
    files.length > 0 ||
    bcc.length > 0 ||
    subject !== (seed.subject ?? '') ||
    !sameMailAddresses(to, seed.to ?? []) ||
    !sameMailAddresses(cc, seed.cc ?? []);

  const title =
    seed.subject !== undefined && seed.subject !== null && seed.subject.trim() !== ''
      ? seed.subject
      : t(TITLE_KEY[mode]);

  const openFieldRoots = () => [subjectRef.current?.root() ?? null, bodyRef.current?.root() ?? null];

  // The visible subject text; chips and all, read off the field rather than a controlled value.
  const syncSubject = () => {
    const root = subjectRef.current?.root();

    setSubject(root?.textContent ?? '');
    // Minimising unmounts the field; the remount re-seeds from this, so typed text and chips survive.
    setSubjectHtml(root?.innerHTML ?? '');
    clearOpenFieldsError();
  };

  const clearOpenFieldsError = () => {
    if (errors.openFields === undefined || collectOpenFields(openFieldRoots()).labels.length > 0) return;

    setErrors(({ openFields: _cleared, ...rest }) => rest);
  };

  // Parent passive effects run after the Vorschau modal's unmount cleanup restores focus
  // to its opener, so the chip focus lands last.
  useEffect(() => {
    if (openFieldFocus === 0) return;

    const { first } = collectOpenFields([subjectRef.current?.root() ?? null, bodyRef.current?.root() ?? null]);

    if (first !== null) selectOpenField(first);
  }, [openFieldFocus]);

  function validate(): FieldErrors {
    const next: FieldErrors = {};
    const { labels: openLabels } = collectOpenFields(openFieldRoots());

    if (openLabels.length > 0) next.openFields = t('error-open-fields', { list: openLabels.join(', ') });

    if (to.length === 0) {
      next.to = t('error-no-recipient');
    } else {
      const invalidTo = firstInvalidAddress(to);

      if (invalidTo !== null) next.to = t('error-invalid-recipient', { value: invalidTo });
    }

    const invalidCc = firstInvalidAddress(cc);

    if (invalidCc !== null) next.cc = t('error-invalid-recipient', { value: invalidCc });

    const invalidBcc = firstInvalidAddress(bcc);

    if (invalidBcc !== null) next.bcc = t('error-invalid-recipient', { value: invalidBcc });

    if (subject.trim() === '') next.subject = t('error-empty-subject');
    else if (subject.length > SUBJECT_MAX_LENGTH)
      next.subject = t('error-subject-too-long', { max: SUBJECT_MAX_LENGTH });

    return next;
  }

  // `files` sizes are known before any read, so the 25 MB cap refuses here too, ahead of the Vorschau.
  function sendGate(): FieldErrors {
    const next = validate();
    const combinedBytes = files.reduce((sum, file) => sum + file.size, forwardBaselineBytes);

    if (combinedBytes > MAX_ATTACHMENT_BYTES) {
      next.attachments = t('error-attachments-too-large', { limit: formatLimit(MAX_ATTACHMENT_BYTES) });
    }

    return next;
  }

  function refuse(refusal: FieldErrors) {
    setErrors(refusal);
    setPreviewOpen(false);

    if (refusal.openFields !== undefined) setOpenFieldFocus((count) => count + 1);
  }

  function requestSend() {
    const refusal = sendGate();

    if (Object.keys(refusal).length > 0) {
      refuse(refusal);

      return;
    }

    setErrors({});
    setPreviewOpen(true);
  }

  async function confirmSend() {
    const refusal = sendGate();

    if (Object.keys(refusal).length > 0) {
      refuse(refusal);

      return;
    }

    setErrors({});
    setProviderRefusal(null);
    setSending(true);

    let fileAttachments: OutboundAttachment[];

    try {
      fileAttachments = await Promise.all(
        files.map(async (file) => ({
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          contentBase64: await fileToBase64(file),
        })),
      );
    } catch {
      setSending(false);
      setPreviewOpen(false);
      toast.danger(t('send-failure'));

      return;
    }

    const attachments = [...forwardAttachments, ...fileAttachments];
    const sendable = toSendableBody(assembledHtml, t('personal-link-text'));
    const personalLinkProspectId = sendable.hasPersonalLink ? (seed.templateContext?.prospectId ?? null) : null;
    const body = {
      to: to.map((address) => ({
        email: address.email,
        ...(address.name !== undefined ? { name: address.name } : {}),
      })),
      cc: cc.map((address) => ({
        email: address.email,
        ...(address.name !== undefined ? { name: address.name } : {}),
      })),
      bcc: bcc.map((address) => ({
        email: address.email,
        ...(address.name !== undefined ? { name: address.name } : {}),
      })),
      subject,
      body: sendable.html,
      attachments,
      ...(personalLinkProspectId !== null ? { personalLinkProspectId } : {}),
      ...(seed.replyToProviderMessageId !== undefined
        ? { replyToProviderMessageId: seed.replyToProviderMessageId }
        : {}),
    };

    try {
      const result = await apiClient.mailbox.send({ pathParams: { workspaceId }, body });

      if (result.ok) {
        toast.success(t('sent-success'));
        onSent?.(result.body);
        onClose();

        return;
      }

      // Keep all content on failure so the member can retry / fix.
      const code =
        result.status === 422 && typeof result.body === 'object' && result.body !== null
          ? (result.body as { error?: { code?: string } }).error?.code
          : undefined;

      const reason = (result.body as { error?: { details?: { reason?: unknown } } } | null)?.error?.details?.reason;

      if (code === 'communications.mailbox.send-refused' && typeof reason === 'string') {
        setProviderRefusal(tRoot('errors.communications.mailbox.send-refused', { reason }));
      } else if (code === 'communications.mailbox.attachments-too-large') {
        toast.danger(t('error-attachments-too-large', { limit: formatLimit(refusedLimitBytes(result.body)) }));
      } else if (code === 'communications.mailbox.personal-link-unavailable') {
        toast.danger(tRoot('errors.communications.mailbox.personal-link-unavailable'));
      } else {
        toast.danger(t('send-failure'));
      }

      setSending(false);
      setPreviewOpen(false);
    } catch {
      toast.danger(t('send-failure'));
      setSending(false);
      setPreviewOpen(false);
    }
  }

  if (minimised) {
    // Docked bar, bottom-right, portaled so it floats above any open composer
    // modal's backdrop (Gmail-style). `dockOffset` spaces multiple bars sideways.
    return createPortal(
      <div
        className="fixed bottom-4 z-[55] flex w-72 items-center gap-2 rounded-t-md border border-border bg-surface px-3 py-2 shadow-md"
        style={{ right: `${1 + dockOffset * 19}rem` }}
      >
        <MailIcon className="h-4 w-4 shrink-0 text-text-tertiary" aria-hidden />
        <Button
          type="button"
          variant="ghost"
          size={null}
          onClick={onMinimiseToggle}
          className="h-auto min-w-0 flex-1 justify-start p-0 text-left text-base-sm text-text-primary hover:bg-transparent"
        >
          {/* A kit `Button` is a flex box, so the ellipsis has to live on the
              label: `truncate` on a flex container never engages, and a flex
              item needs `min-w-0` before it may shrink below its content width
              (BL-066/BL-072). */}
          <span className="min-w-0 truncate">{title}</span>
        </Button>
        {/* The two docked-bar affordances are `ghost` at 20px rather than the
            kit's 36px: the bar is a `py-2` strip built around 16px glyphs, and
            the token footprint would have made it half again as tall.
            `size={null}` — not `size="icon"` plus an `h-5 w-5` override —
            because until IMM-785 `cn` did not classify `h-control-md` as a
            height, so a `className` override never beat it (BL-103);
            `size={null}` stays because it states that intent directly. */}
        <Button
          type="button"
          variant="ghost"
          size={null}
          aria-label={t('restore')}
          onClick={onMinimiseToggle}
          className="h-5 w-5 shrink-0 text-text-tertiary hover:bg-transparent hover:text-text-primary"
        >
          <ChevronDownIcon className="h-4 w-4 rotate-180" aria-hidden />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size={null}
          aria-label={t('close')}
          onClick={onClose}
          className="h-5 w-5 shrink-0 text-text-tertiary hover:bg-transparent hover:text-text-primary"
        >
          <XIcon className="h-4 w-4" aria-hidden />
        </Button>
      </div>,
      document.body,
    );
  }

  // Shared body — reused by the modal (floating compose) and the embedded reply box.
  // The header field rows are a full-bleed ruled stack: each row is borderless with
  // a full-width divider beneath, so the rules — not per-field boxes — are the only
  // boundaries (design). `padX` keeps content aligned with the padded body below;
  // the group's negative margins bleed the rules to the panel edges (`negX`) and
  // pull the stack up under the modal header divider (`bleedTop`).
  const padX = embedded ? 'px-3' : 'px-6';
  const negX = embedded ? '-mx-3' : '-mx-6';
  const bleedTop = embedded ? '-mt-3' : '-mt-6';
  const composerBody = (
    <>
      <div className={cn(negX, bleedTop, 'flex min-h-0 flex-1 flex-col')}>
        {providerRefusal !== null && (
          <div className={cn(padX, 'py-3')}>
            <InfoBox type="danger" icon={<WarningTriangleIcon className="h-4 w-4" />}>
              <p>{providerRefusal}</p>
            </InfoBox>
          </div>
        )}
        {/* From is read-only — the sender is the connected mailbox, not a choice. The
            dropdown chevron is a placeholder for future mailbox switching (disabled).
            Hidden inline: a reply always sends from the account that received it. */}
        {!embedded && (
          <ComposerFieldRow
            label={t('from')}
            htmlFor="composer-from"
            padX={padX}
            trailing={<ChevronDownIcon className="h-4 w-4 shrink-0 text-text-tertiary opacity-60" aria-hidden />}
          >
            <input
              id="composer-from"
              type="text"
              value={connectedEmail ?? ''}
              disabled
              aria-label={t('from')}
              className="w-full bg-transparent py-1 text-base-sm text-text-primary outline-none disabled:cursor-not-allowed disabled:opacity-60"
            />
          </ComposerFieldRow>
        )}

        <ComposerFieldRow
          label={t('to')}
          htmlFor="composer-to"
          padX={padX}
          error={errors.to}
          trailing={
            // The Cc/Bcc toggle needs no geometry pinned back at all: its `h-8`
            // is `--height-control-sm` and its `px-3` is `size="sm"`'s, so the
            // unpressed state IS the kit's `secondary` variant bar its softer
            // text colour. Only the pressed state overrides — the kit has no
            // "on" treatment for a toggle that is not a full segmented control.
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setShowCcBcc((prev) => !prev)}
              aria-pressed={showCcBcc}
              className={cn(
                'shrink-0 rounded-lg text-base-sm',
                showCcBcc
                  ? 'border-interactive bg-interactive/10 text-text-primary hover:bg-interactive/10'
                  : 'font-normal text-text-secondary hover:text-text-primary',
              )}
            >
              {t('cc-bcc-toggle')}
            </Button>
          }
        >
          <ComposerRecipientField
            id="composer-to"
            label={t('to')}
            value={to}
            onChange={setTo}
            workspaceId={workspaceId}
            placeholder={t('recipient-placeholder')}
            invalid={errors.to !== undefined}
          />
        </ComposerFieldRow>

        {showCcBcc && (
          <>
            <ComposerFieldRow label={t('cc')} htmlFor="composer-cc" padX={padX} error={errors.cc}>
              <ComposerRecipientField
                id="composer-cc"
                label={t('cc')}
                value={cc}
                onChange={setCc}
                workspaceId={workspaceId}
                placeholder={t('recipient-placeholder')}
                invalid={errors.cc !== undefined}
              />
            </ComposerFieldRow>
            <ComposerFieldRow label={t('bcc')} htmlFor="composer-bcc" padX={padX} error={errors.bcc}>
              <ComposerRecipientField
                id="composer-bcc"
                label={t('bcc')}
                value={bcc}
                onChange={setBcc}
                workspaceId={workspaceId}
                placeholder={t('recipient-placeholder')}
                invalid={errors.bcc !== undefined}
              />
            </ComposerFieldRow>
          </>
        )}

        {/* Subject is hidden inline — the seed already carries the Re:/Fwd:-prefixed
            subject (per the picked action), which is what gets sent. */}
        {!embedded && (
          <ComposerFieldRow label={t('subject')} htmlFor="composer-subject" padX={padX} error={errors.subject}>
            <ChipLineField
              ref={subjectRef}
              id="composer-subject"
              variant="inline"
              maxLength={SUBJECT_MAX_LENGTH}
              defaultValue={subjectHtml}
              labels={placeholderLabel}
              onChange={syncSubject}
              ariaLabel={t('subject')}
              invalid={errors.subject !== undefined}
            />
          </ComposerFieldRow>
        )}

        {/* Rich-text body — formatting toolbar + editable area, flush under the
            subject rule (the toolbar's rule is the subject/body separator). */}
        <ComposerBodyEditor
          ref={bodyRef}
          label={t('title-new')}
          defaultHtml={bodyHtml}
          onChange={(html) => {
            setBodyHtml(html);
            setProviderRefusal(null);
            clearOpenFieldsError();
          }}
          onAttach={openFilePicker}
          {...(mode === 'new' && !embedded
            ? {
                toolbarEnd: (
                  <TemplateMenu
                    workspaceId={workspaceId}
                    onPick={(id) => void insertTemplate(id)}
                    {...(seed.templateArea !== undefined ? { area: seed.templateArea } : {})}
                  />
                ),
              }
            : {})}
          padX={padX}
          t={t}
          {...(embedded ? { placeholder: t('reply-placeholder') } : {})}
          {...(hasSignature(seed.signatureHtml)
            ? {
                belowBody: <ComposerSignature html={seed.signatureHtml} padX={padX} label={t('signature-label')} />,
              }
            : {})}
        />
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(event) => {
          addFiles(event.target.files);
          // Reset so re-selecting the same file re-fires change.
          event.target.value = '';
        }}
      />
      <ComposerAttachments files={files} onChange={setFiles} t={t} baselineBytes={forwardBaselineBytes} />
      {errors.attachments !== undefined && (
        <p role="alert" className="text-xs text-danger-600">
          {errors.attachments}
        </p>
      )}
      {errors.openFields !== undefined && (
        <p role="alert" className="text-sm text-danger-600">
          {errors.openFields}
        </p>
      )}
    </>
  );

  const footerButtons = (
    <div className="flex w-full items-center justify-between gap-3">
      {/* Left: the primary send, then preview. */}
      <div className="flex items-center gap-2">
        {/* `loading`, not `disabled` + a „Wird gesendet…" label (IMM-736.1): the
            send is in flight, the control is not unavailable. `loading` keeps
            the primary fill, announces `aria-busy` and locks the button out on
            its own, so the label stays put instead of two mechanisms reporting
            the same state differently. */}
        <Button type="button" variant="primary" loading={sending} onClick={requestSend}>
          <SendIcon className="h-4 w-4" aria-hidden />
          {t('send')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          disabled={assembledHtml.trim() === ''}
          onClick={() => setPreviewOpen(true)}
        >
          <EyeIcon className="h-4 w-4" aria-hidden />
          {t('preview')}
        </Button>
      </div>
      {/* Right: attach, then discard. */}
      <div className="flex items-center gap-2">
        <Button type="button" variant="ghost" onClick={openFilePicker}>
          <PaperclipIcon className="h-4 w-4" aria-hidden />
          {t('attachment-add')}
        </Button>
        <Button type="button" variant="ghost" className="text-danger-600 hover:text-danger-700" onClick={onClose}>
          <XIcon className="h-4 w-4" aria-hidden />
          {t('discard')}
        </Button>
      </div>
    </div>
  );

  // Vorschau (design 02): the mail exactly as the recipient sees it — sender header,
  // subject, the assembled + sanitised body, and the attachments — read-only.
  const previewBadge = provider !== undefined && provider !== null ? provider : null;
  const previewAttachments = [
    ...files.map((file) => ({ name: file.name, meta: `${fileExtLabel(file.name)} · ${formatBytes(file.size)}` })),
    ...forwardAttachments.map((attachment) => ({
      name: attachment.filename,
      meta: `${fileExtLabel(attachment.filename)} · ${formatBytes(base64DecodedLength(attachment.contentBase64))}`,
    })),
  ];
  const previewModal = previewOpen ? (
    <Modal
      open
      onClose={() => setPreviewOpen(false)}
      size="lg"
      placement="right"
      className="max-w-[760px]"
      confirmOnClose={false}
      closeDisabled={sending}
      dismissOnBackdrop
      title={t('preview')}
      description={t('preview-subtitle')}
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <Button type="button" variant="ghost" disabled={sending} onClick={() => setPreviewOpen(false)}>
            <ChevronLeftIcon className="h-4 w-4" aria-hidden />
            {t('preview-back')}
          </Button>
          <Button type="button" variant="primary" loading={sending} onClick={() => void confirmSend()}>
            <SendIcon className="h-4 w-4" aria-hidden />
            {t('send')}
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Sender / recipient header — how the mail lands in the recipient's inbox.
            `-mx-6 px-6` bleeds the divider to the side-panel edges (Modal body px-6). */}
        <div className="-mx-6 flex items-center gap-3 border-b border-border px-6 pb-4">
          <MailboxAvatar email={connectedEmail} size="lg" className="h-9 w-9" />
          <div className="min-w-0 flex-1">
            <p className="truncate font-semibold text-text-primary">{connectedEmail ?? ''}</p>
            <p className="truncate text-base-sm text-text-secondary">
              {t('to')}: {to.map((address) => address.email).join(', ')}
            </p>
            {cc.length > 0 && (
              <p className="truncate text-base-sm text-text-secondary">
                {t('cc')}: {cc.map((address) => address.email).join(', ')}
              </p>
            )}
            {bcc.length > 0 && (
              <p className="truncate text-base-sm text-text-secondary">
                {t('bcc')}: {bcc.map((address) => address.email).join(', ')}
              </p>
            )}
          </div>
          {previewBadge !== null && (
            <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border bg-canvas px-2.5 py-1 text-xs text-text-secondary">
              <span className={cn('h-1.5 w-1.5 rounded-full', PROVIDER_DOT[previewBadge])} aria-hidden />
              {PROVIDER_BRAND[previewBadge]}
            </span>
          )}
        </div>

        {subject.trim() !== '' && (
          <h2 className="-mx-6 border-b border-border px-6 pb-5 text-lg font-semibold text-text-primary">{subject}</h2>
        )}

        <div
          className="max-w-none text-base-sm text-text-primary [&_a]:text-interactive [&_blockquote]:border-l [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:text-text-secondary [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6"
          dangerouslySetInnerHTML={{ __html: previewHtml }}
        />

        {previewAttachments.length > 0 && (
          <ul className="space-y-2">
            {previewAttachments.map((attachment, index) => (
              <li
                key={`${attachment.name}-${index}`}
                className="flex items-center gap-3 rounded-md border border-border bg-surface px-3 py-2"
              >
                <FileIcon className="h-5 w-5 shrink-0 text-text-tertiary" aria-hidden />
                <div className="min-w-0">
                  <p className="truncate text-base-sm text-text-primary">{attachment.name}</p>
                  <p className="text-xs text-text-tertiary">{attachment.meta}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  ) : null;

  // Inline reply within the thread panel (design 02): a reply box docked under the
  // conversation — no modal chrome, and no box of its own either. The host (the
  // thread message card) owns the border and background so the composer's rows read
  // as additional full-width sections of that one card.
  if (embedded) {
    return (
      <div className="flex w-full flex-col">
        <div className="space-y-4 p-3">
          {composerBody}
          <div className={cn('flex items-center justify-end gap-3 border-t border-border pt-3', negX, padX)}>
            {footerButtons}
          </div>
        </div>
        {previewModal}
      </div>
    );
  }

  // Floating compose (new / reply from a list) — a 760px right side-panel (design
  // Screen 4, `Imm233SidePanel`), with a minimise affordance in the header that
  // docks it to the bottom-right (Gmail-style).
  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      placement="right"
      className="max-w-[760px]"
      dirty={isDirty}
      title={title}
      headerActions={
        <Button
          type="button"
          variant="ghost"
          size={null}
          aria-label={t('minimise')}
          onClick={onMinimiseToggle}
          className="h-8 w-8 shrink-0 text-text-tertiary hover:text-text-primary"
        >
          <ChevronDownIcon className="h-4 w-4" aria-hidden />
        </Button>
      }
      footer={footerButtons}
    >
      <div className="flex h-full flex-col gap-4">{composerBody}</div>
      {previewModal}
    </Modal>
  );
}
